import { useCallback, useEffect, useState } from "react";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
import type { Tenant } from "../../models/tenant";
import bulkStyles from "./Bulk/BulkActions.module.css";
import BulkSelectionCard, {
  BulkSelectionCardRow,
} from "./Bulk/BulkSelectionCard";
import BulkPdfPreview from "./Bulk/BulkPdfPreview";
import { usePdfPreviewCache } from "./Bulk/usePdfPreviewCache";
import type { BulkResult, BulkTenantModalProps } from "./Bulk/types";
import { useBulkResultsToast } from "./Bulk/useBulkResultsToast";
import { useBatchSelection } from "./Bulk/useBatchSelection";

interface RevisionComputeResult extends BulkResult {
  ancien_loyer?: number;
  ancien_charges?: number;
  nouveau_loyer?: number;
  nouveau_charges?: number;
  trimestre?: number;
  previous_indice_year?: number;
  new_indice_year?: number;
}

interface BulkRevisionLoyerFormValues {
  batchIds: number[];
  sendEmail: boolean;
}

export default function BulkRevisionLoyerModal({
  open,
  onOpenChange,
  selectedTenantIds,
  resetSelection,
}: BulkTenantModalProps) {
  return (
    <ModalDialog
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      header="Réviser les loyers"
    >
      <BulkRevisionLoyerContent
        close={() => onOpenChange(false)}
        selectedTenantIds={selectedTenantIds}
        resetSelection={resetSelection}
      />
    </ModalDialog>
  );
}

interface BulkRevisionLoyerContentProps {
  close: () => void;
  selectedTenantIds: number[];
  resetSelection?: () => void;
}

function BulkRevisionLoyerContent({
  close,
  selectedTenantIds,
  resetSelection,
}: BulkRevisionLoyerContentProps) {
  const {
    previewRevisionLoyerPdf,
    bulkRevisionLoyer,
    bulkComputeRevisionLoyer,
    tenantsById,
  }: {
    previewRevisionLoyerPdf: (params: { tenantId: number }) => Promise<string>;
    bulkRevisionLoyer: (params: {
      tenantIds: number[];
      sendEmail: boolean;
    }) => Promise<{ results: BulkResult[] }>;
    bulkComputeRevisionLoyer: (params: {
      tenantIds: number[];
    }) => Promise<{ results: RevisionComputeResult[] }>;
    tenantsById: Record<number, Tenant>;
  } = useTenants({ withById: true });

  const notifyBulkResults = useBulkResultsToast(tenantsById);
  const [computeByTenantId, setComputeByTenantId] = useState<
    Record<number, RevisionComputeResult>
  >({});

  useEffect(() => {
    if (selectedTenantIds.length === 0) return;
    let cancelled = false;
    bulkComputeRevisionLoyer({ tenantIds: selectedTenantIds })
      .then(({ results: computeResults }) => {
        if (cancelled) return;
        setComputeByTenantId(
          Object.fromEntries(computeResults.map((r) => [r.tenant_id, r])),
        );
      })
      .catch(() => {
        // Per-card display falls back to "current rent only" if compute fails.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantIds, bulkComputeRevisionLoyer]);

  async function handleSubmit(
    values: BulkRevisionLoyerFormValues,
    { setSubmitting, setStatus }: FormikHelpers<BulkRevisionLoyerFormValues>,
  ) {
    setStatus(undefined);
    try {
      const response = await bulkRevisionLoyer({
        tenantIds: values.batchIds,
        sendEmail: values.sendEmail,
      });
      notifyBulkResults(
        response.results,
        ({ successCount, failureCount, total }) => {
          const s = successCount > 1 ? "s" : "";
          const sf = failureCount > 1 ? "s" : "";
          return {
            successSection: `Révision${s} effectuée${s}${
              values.sendEmail ? " et envoyée par mail" : ""
            }`,
            failureSection: `Révision${sf} non effectuée${sf}`,
            successToastTitle:
              failureCount === 0
                ? `${successCount} révision${s} de loyer effectuée${s}`
                : `${successCount} révision${s} sur ${total} effectuée${s}`,
            errorToastTitle: "Aucune révision effectuée",
          };
        },
      );
      resetSelection?.();
      close();
    } catch (e) {
      setStatus(handleAPIError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik<BulkRevisionLoyerFormValues>
      initialValues={{
        batchIds: selectedTenantIds,
        sendEmail: false,
      }}
      onSubmit={handleSubmit}
    >
      <Form>
        <BulkRevisionLoyerFields
          previewRevisionLoyerPdf={previewRevisionLoyerPdf}
          tenantsById={tenantsById}
          computeByTenantId={computeByTenantId}
          close={close}
        />
      </Form>
    </Formik>
  );
}

interface BulkRevisionLoyerFieldsProps {
  previewRevisionLoyerPdf: (params: { tenantId: number }) => Promise<string>;
  tenantsById: Record<number, Tenant>;
  computeByTenantId: Record<number, RevisionComputeResult>;
  close: () => void;
}

function BulkRevisionLoyerFields({
  previewRevisionLoyerPdf,
  tenantsById,
  computeByTenantId,
  close,
}: BulkRevisionLoyerFieldsProps) {
  const { status } = useFormikContext<BulkRevisionLoyerFormValues>();

  const {
    batchIds,
    batchTenants,
    activeTenantId,
    setActiveTenantId,
    removeFromBatch,
  } = useBatchSelection(tenantsById);

  const activeCompute =
    activeTenantId !== null ? computeByTenantId[activeTenantId] : undefined;
  const activeIsIneligible =
    activeCompute !== undefined && !activeCompute.success;

  const fetcher = useCallback(
    ({ tenantId }: { tenantId: number }) =>
      previewRevisionLoyerPdf({ tenantId }),
    [previewRevisionLoyerPdf],
  );
  const { previewUrl, previewLoading, previewError } = usePdfPreviewCache({
    activeKey: activeTenantId !== null ? String(activeTenantId) : null,
    activePayload:
      activeTenantId !== null ? { tenantId: activeTenantId } : null,
    fetcher,
    enabled: !activeIsIneligible,
  });

  return (
    <>
      <ModalDialogBody className={bulkStyles.body}>
        <Text color="muted">
          Vous avez {batchIds.length} révision{batchIds.length > 1 ? "s" : ""}{" "}
          de loyer à effectuer.
        </Text>

        <div className={bulkStyles.split}>
          <div className={bulkStyles.cardList}>
            {batchTenants.length === 0 && (
              <Text color="muted">Aucun locataire sélectionné.</Text>
            )}
            {batchTenants.map((tenant) => {
              const compute = computeByTenantId[tenant.id];
              const isIneligible = Boolean(compute && !compute.success);
              const ancienLoyer = compute?.ancien_loyer ?? tenant.loyer_hc ?? 0;
              const ancienCharges =
                compute?.ancien_charges ?? tenant.charges ?? 0;
              return (
                <BulkSelectionCard
                  key={tenant.id}
                  fileName="Révision_de_loyer.pdf"
                  tenantName={tenant.nom}
                  isActive={tenant.id === activeTenantId}
                  isIneligible={isIneligible}
                  ineligibleReason={compute?.error}
                  onSelect={() => setActiveTenantId(tenant.id)}
                  onRemove={() => removeFromBatch(tenant.id)}
                >
                  <BulkSelectionCardRow label="Loyer HC">
                    {currencyFormatter(ancienLoyer)}
                    {compute?.success && (
                      <>
                        {" → "}
                        <Text as="span" variant="deci" weight="bold">
                          {currencyFormatter(compute.nouveau_loyer ?? 0)}
                        </Text>
                      </>
                    )}
                  </BulkSelectionCardRow>
                  <BulkSelectionCardRow label="Charges">
                    {currencyFormatter(ancienCharges)}
                    {compute?.success && (
                      <>
                        {" → "}
                        <Text as="span" variant="deci" weight="bold">
                          {currencyFormatter(compute.nouveau_charges ?? 0)}
                        </Text>
                      </>
                    )}
                  </BulkSelectionCardRow>
                  {compute?.success && (
                    <BulkSelectionCardRow label={`IRL T${compute.trimestre}`}>
                      <Text as="span" variant="deci" weight="bold">
                        {compute.previous_indice_year} →{" "}
                        {compute.new_indice_year}
                      </Text>
                    </BulkSelectionCardRow>
                  )}
                </BulkSelectionCard>
              );
            })}
          </div>

          <BulkPdfPreview
            iframeTitle="Aperçu révision de loyer"
            hasSelection={activeTenantId !== null}
            isIneligible={activeIsIneligible}
            ineligibleTitle="Révision impossible pour ce locataire"
            ineligibleReason={activeCompute?.error}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
          />
        </div>

        <Callout variant="danger">{status}</Callout>
      </ModalDialogBody>
      <ModalDialogFooter
        start={
          <Checkbox name="sendEmail">Envoyer ce document par mail</Checkbox>
        }
      >
        <Button variant="secondary" type="button" onClick={close}>
          Annuler
        </Button>
        <FormikSubmitButton disabled={batchIds.length === 0}>
          Réviser
        </FormikSubmitButton>
      </ModalDialogFooter>
    </>
  );
}
